import React from "react";
import ApprovalField from "./ApprovalField";
import { MdInsertComment } from "react-icons/md";
import AttachmentPopover from "../../../pages/employee_details/InvestmentsAndProofs/components/AttachmentPopover";

const RentalIncomeSection = ({
  rentalIncomeData = [],
  homeLoanData,
  onApproval,
  onRejection,
  isApproving,
  isRejecting,
  taxRegime,
  // Add proof and comment handlers
  attachments,
  onFileSelect,
  onAddMoreAttachments,
  onRemoveAttachment,
  onOpenImage,
  openPopoverKey,
  setOpenPopoverKey,
  onCommentClick,
}) => {
  // For new tax regime, only show rental income (let out properties)
  // For old tax regime, show both rental income and self occupied property
  
  const hasRentalIncome = rentalIncomeData && rentalIncomeData.length > 0;
  const hasSelfOccupied = homeLoanData && taxRegime === 'old';
  
  if (!hasRentalIncome && !hasSelfOccupied) return null;

  return (
    <>
      {/* Let Out Property Sections - Show for both regimes */}
      {hasRentalIncome && rentalIncomeData.map((property, index) => {
        // Calculate total declared amount for this property
        const annualRent = parseFloat(property.annual_rent_received || 0);
        const municipalTaxes = parseFloat(property.municipal_taxes_paid || 0);
        const interestAmount = property.repaying_home_loan === "1" ? parseFloat(property.interest_amount || 0) : 0;
        // For old tax regime, include principal amount in total declared amount
        const principalAmount = (taxRegime === 'old' && property.repaying_home_loan === "1") ? parseFloat(property.principal_amount || 0) : 0;
        const totalDeclaredAmount = annualRent + municipalTaxes + interestAmount + principalAmount;
        // Compute approved amount simply by summing the individual approved fields returned by the API
        const totalApprovedAmount =
          (parseFloat(property.annual_rent_rec_approved_amount || 0)) +
          (parseFloat(property.municipal_tax_amount_approved || 0)) +
          (parseFloat(property.interest_amount_approved || 0)) +
          (parseFloat(property.principal_amount_approved || 0));
        
        return (
          <div key={property.rental_income_id || index}>
            <p className="text-lg mx-3 font-semibold">Letout Property {index + 1}</p>
            <p className="mx-3 mt-1 mb-4 text-sm">
              Declared Amount: ₹{totalDeclaredAmount.toLocaleString()} | 
              Approved Amount: ₹{parseFloat(totalApprovedAmount || 0).toLocaleString()}
            </p>

            {/* Annual Rent Received */}
            <div className="flex items-center mx-3 my-3">
              <div className="flex-no-grow-no-shrink px-1.5 w-[28.4%]">
                <p>Annual Rent Received</p>
              </div>
              <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%]">
                <p className="text-right text-sm">₹{parseFloat(property.annual_rent_received || 0).toLocaleString()}</p>
              </div>
              <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
                <AttachmentPopover
                  attachmentKey={`rental-annual-rent-${property.rental_income_id}`}
                  attachments={attachments}
                  onFileSelect={() =>
                    onFileSelect(
                      `rental-annual-rent-${property.rental_income_id}`,
                      {
                        rental_income_id: property.rental_income_id,
                        type: "annual_rent_received",
                      },
                      property.document_count?.annual_rent_received || 0
                    )
                  }
                  onAddMoreAttachments={onAddMoreAttachments}
                  onRemoveAttachment={onRemoveAttachment}
                  onOpenImage={onOpenImage}
                  openPopoverKey={openPopoverKey}
                  setOpenPopoverKey={setOpenPopoverKey}
                  documentCount={property.document_count?.annual_rent_received || 0}
                />
              </div>
              <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
                <button
                  onClick={() =>
                    onCommentClick(`rental-annual-rent-${property.rental_income_id}`, {
                      rental_income_id: property.rental_income_id,
                      type: "annual_rent_received",
                    })
                  }
                  className="flex items-center"
                >
                  <MdInsertComment className="w-5 h-5 text-neutral-600" />
                  {(property.comment_count?.annual_rent_received || 0) > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      {property.comment_count.annual_rent_received}
                    </span>
                  )}
                </button>
              </div>
              <ApprovalField
                // use approved amount from API when present
                approvedAmount={property.annual_rent_rec_approved_amount ?? null}
                actualAmount={parseFloat(property.annual_rent_received || 0)}
                onApproval={(data) => {
                  // Send specific key expected by API for annual rent approval
                  onApproval({
                    rental_income_id: property.rental_income_id,
                    annual_rent_rec_approved_amount: data.amount_approved,
                  });
                }}
                onRejection={() => {
                  onRejection({
                    rental_income_id: property.rental_income_id,
                    annual_rent_rec_status: "REJECTED",
                  });
                }}
                approvalParams={{
                  rental_income_id: property.rental_income_id,
                }}
                isApproving={isApproving}
                isRejecting={isRejecting}
                status={property.annual_rent_rec_status || "APPROVAL_AWAITING"}
              />
            </div>
            {/* Municipal Taxes Paid */}
            <div className="flex items-center mx-3 my-3">
              <div className="flex-no-grow-no-shrink px-1.5 w-[28.4%]">
                <p>Municipal Taxes Paid</p>
              </div>
              <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%]">
                <p className="text-right text-sm">₹{parseFloat(property.municipal_taxes_paid || 0).toLocaleString()}</p>
              </div>
              <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
                <AttachmentPopover
                  attachmentKey={`rental-municipal-taxes-${property.rental_income_id}`}
                  attachments={attachments}
                  onFileSelect={() =>
                    onFileSelect(
                      `rental-municipal-taxes-${property.rental_income_id}`,
                      {
                        rental_income_id: property.rental_income_id,
                        type: "municipal_taxes_paid",
                      },
                      property.document_count?.municipal_taxes_paid || 0
                    )
                  }
                  onAddMoreAttachments={onAddMoreAttachments}
                  onRemoveAttachment={onRemoveAttachment}
                  onOpenImage={onOpenImage}
                  openPopoverKey={openPopoverKey}
                  setOpenPopoverKey={setOpenPopoverKey}
                  documentCount={property.document_count?.municipal_taxes_paid || 0}
                />
              </div>
              <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
                <button
                  onClick={() =>
                    onCommentClick(`rental-municipal-taxes-${property.rental_income_id}`, {
                      rental_income_id: property.rental_income_id,
                      type: "municipal_taxes_paid",
                    })
                  }
                  className="flex items-center"
                >
                  <MdInsertComment className="w-5 h-5 text-neutral-600" />
                  {(property.comment_count?.municipal_taxes_paid || 0) > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      {property.comment_count.municipal_taxes_paid}
                    </span>
                  )}
                </button>
              </div>
              <ApprovalField
                approvedAmount={property.municipal_tax_amount_approved ?? null}
                actualAmount={parseFloat(property.municipal_taxes_paid || 0)}
                onApproval={(data) => {
                  // Send specific key expected by API for municipal tax approval
                  onApproval({
                    rental_income_id: property.rental_income_id,
                    municipal_tax_amount_approved: data.amount_approved,
                  });
                }}
                onRejection={() => {
                  onRejection({
                    rental_income_id: property.rental_income_id,
                    municipal_tax_status: "REJECTED",
                  });
                }}
                approvalParams={{
                  rental_income_id: property.rental_income_id,
                }}
                isApproving={isApproving}
                isRejecting={isRejecting}
                status={property.municipal_tax_status || "APPROVAL_AWAITING"}
              />
            </div>
            {/* Interest Paid on Home Loan (if applicable) */}
            {property.repaying_home_loan === "1" && (
              <div className="flex items-center mx-3 my-3">
                <div className="flex-no-grow-no-shrink px-1.5 w-[28.4%]">
                  <p>Interest Paid on Home Loan</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Lender Details: {property.name_of_lender} ({property.lender_pan})
                  </p>
                </div>
                <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%]">
                  <p className="text-right text-sm">₹{parseFloat(property.interest_amount || 0).toLocaleString()}</p>
                </div>
                <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
                  <AttachmentPopover
                    attachmentKey={`rental-interest-${property.rental_income_id}`}
                    attachments={attachments}
                    onFileSelect={() =>
                      onFileSelect(
                        `rental-interest-${property.rental_income_id}`,
                        {
                          rental_income_id: property.rental_income_id,
                          type: "interest_amount",
                        },
                        property.document_count?.interest_amount || 0
                      )
                    }
                    onAddMoreAttachments={onAddMoreAttachments}
                    onRemoveAttachment={onRemoveAttachment}
                    onOpenImage={onOpenImage}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    documentCount={property.document_count?.interest_amount || 0}
                  />
                </div>
                <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
                  <button
                    onClick={() =>
                      onCommentClick(`rental-interest-${property.rental_income_id}`, {
                        rental_income_id: property.rental_income_id,
                        type: "interest_amount",
                      })
                    }
                    className="flex items-center"
                  >
                    <MdInsertComment className="w-5 h-5 text-neutral-600" />
                    {(property.comment_count?.interest_amount || 0) > 0 && (
                      <span className="ml-1 text-xs text-gray-600">
                        {property.comment_count.interest_amount}
                      </span>
                    )}
                  </button>
                </div>
                <ApprovalField
                  // Read existing approved value using the exact get-user-response field name
                  approvedAmount={property.interest_amount_approved ?? null}
                  actualAmount={parseFloat(property.interest_amount || 0)}
                  onApproval={(data) => {
                    // Send interest field key for rental-income related home loan interest
                    onApproval({
                      rental_income_id: property.rental_income_id,
                      // Changed from interest_approved_amount to interest_amount_approved
                      interest_amount_approved: data.amount_approved,
                    });
                  }}
                  onRejection={() => {
                    onRejection({
                      rental_income_id: property.rental_income_id,
                      int_amt_approved_status: "REJECTED",
                    });
                  }}
                  approvalParams={{
                    rental_income_id: property.rental_income_id,
                  }}
                  isApproving={isApproving}
                  isRejecting={isRejecting}
                  // Use the exact status field returned by get-user-response (if present)
                  status={property.int_amt_approved_status || "APPROVAL_AWAITING"}
                />
              </div>
            )}

            {/* Principal Paid on Home Loan for Let Out Property - Only for Old Tax Regime */}
            {taxRegime === 'old' && property.repaying_home_loan === "1" && (
              <div className="flex items-center mx-3 my-3">
                <div className="flex-no-grow-no-shrink px-1.5 w-[28.4%]">
                  <p>Principal Paid on Home Loan for Let Out Property - {index + 1}</p>
                </div>
                <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%]">
                  <p className="text-right text-sm">₹{parseFloat(property.principal_amount || 0).toLocaleString()}</p>
                </div>
                <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
                  <AttachmentPopover
                    attachmentKey={`rental-principal-${property.rental_income_id}`}
                    attachments={attachments}
                    onFileSelect={() =>
                      onFileSelect(
                        `rental-principal-${property.rental_income_id}`,
                        {
                          rental_income_id: property.rental_income_id,
                          type: "principal_amount",
                        },
                        property.document_count?.principal_amount || 0
                      )
                    }
                    onAddMoreAttachments={onAddMoreAttachments}
                    onRemoveAttachment={onRemoveAttachment}
                    onOpenImage={onOpenImage}
                    openPopoverKey={openPopoverKey}
                    setOpenPopoverKey={setOpenPopoverKey}
                    documentCount={property.document_count?.principal_amount || 0}
                  />
                </div>
                <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
                  <button
                    onClick={() =>
                      onCommentClick(`rental-principal-${property.rental_income_id}`, {
                        rental_income_id: property.rental_income_id,
                        type: "principal_amount",
                      })
                    }
                    className="flex items-center"
                  >
                    <MdInsertComment className="w-5 h-5 text-neutral-600" />
                    {(property.comment_count?.principal_amount || 0) > 0 && (
                      <span className="ml-1 text-xs text-gray-600">
                        {property.comment_count.principal_amount}
                      </span>
                    )}
                  </button>
                </div>
                <ApprovalField
                  // Read existing approved value using the exact get-user-response field name
                  approvedAmount={property.principal_amount_approved ?? null}
                  actualAmount={parseFloat(property.principal_amount || 0)}
                  onApproval={(data) => {
                    // Send principal approval key for rental-income's principal
                    onApproval({
                      rental_income_id: property.rental_income_id,
                      // Changed from principal_approved_amount to principal_amount_approved
                      principal_amount_approved: data.amount_approved,
                    });
                  }}
                  onRejection={() => {
                    onRejection({
                      rental_income_id: property.rental_income_id,
                      p_amt_approved_status: "REJECTED",
                    });
                  }}
                  approvalParams={{
                    rental_income_id: property.rental_income_id,
                  }}
                  isApproving={isApproving}
                  isRejecting={isRejecting}
                  // Use the exact status field returned by get_user_response (if present)
                  status={property.p_amt_approved_status || "APPROVAL_AWAITING"}
                />
              </div>
            )}
            
            <hr className="my-5 mx-3" />
          </div>
        );
      })}

      {/* Self Occupied Property - Only show for Old Tax Regime */}
      {hasSelfOccupied && (
        <>
          <p className="text-lg mx-3 font-semibold">Self Occupied Property</p>
          <p className="mx-3 mt-1 mb-4 text-sm">
            Declared Amount: ₹{(parseFloat(homeLoanData.principal_amount || 0) + parseFloat(homeLoanData.interest_amount || 0)).toLocaleString()} |{" "}
            {/* Sum approved principal and interest returned by API */}
            Approved Amount: ₹{(parseFloat(homeLoanData.principal_amount_approved || 0) + parseFloat(homeLoanData.interest_amount_approved || 0)).toLocaleString()}
          </p>

          {/* Principal Paid on Home Loan */}
          <div className="flex items-center mx-3 my-3">
            <div className="flex-no-grow-no-shrink px-1.5 w-[28.4%]">
              <p>Principal Paid on Home Loan</p>
            </div>
            <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%]">
              <p className="text-right text-sm">₹{parseFloat(homeLoanData.principal_amount || 0).toLocaleString()}</p>
            </div>
            <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
              <AttachmentPopover
                attachmentKey={`home-loan-principal-${homeLoanData.home_loan_id}`}
                attachments={attachments}
                onFileSelect={() =>
                  onFileSelect(
                    `home-loan-principal-${homeLoanData.home_loan_id}`,
                    {
                      home_loan_id: homeLoanData.home_loan_id,
                      type: "principal_amount",
                    },
                    homeLoanData.document_count?.principal_amount || 0
                  )
                }
                onAddMoreAttachments={onAddMoreAttachments}
                onRemoveAttachment={onRemoveAttachment}
                onOpenImage={onOpenImage}
                openPopoverKey={openPopoverKey}
                setOpenPopoverKey={setOpenPopoverKey}
                documentCount={homeLoanData.document_count?.principal_amount || 0}
              />
            </div>
            <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
              <button
                onClick={() =>
                  onCommentClick(`home-loan-principal-${homeLoanData.home_loan_id}`, {
                    home_loan_id: homeLoanData.home_loan_id,
                    type: "principal_amount",
                  })
                }
                className="flex items-center"
              >
                <MdInsertComment className="w-5 h-5 text-neutral-600" />
                {(homeLoanData.comment_count?.principal_amount || 0) > 0 && (
                  <span className="ml-1 text-xs text-gray-600">
                    {homeLoanData.comment_count.principal_amount}
                  </span>
                )}
              </button>
            </div>
            <ApprovalField
              // Read existing approved value using the exact get-user-response field name
              approvedAmount={homeLoanData.principal_amount_approved ?? null}
              actualAmount={parseFloat(homeLoanData.principal_amount || 0)}
              onApproval={(data) => {
                // Send principal approval key for home loan
                onApproval({
                  home_loan_id: homeLoanData.home_loan_id,
                  // Changed from principal_approved_amount to principal_amount_approved
                  principal_amount_approved: data.amount_approved,
                });
              }}
              onRejection={() => {
                onRejection({
                  home_loan_id: homeLoanData.home_loan_id,
                  p_amt_approved_status: "REJECTED",
                });
              }}
              approvalParams={{
                home_loan_id: homeLoanData.home_loan_id,
              }}
              isApproving={isApproving}
              isRejecting={isRejecting}
              // Use the exact status field returned by get-user-response (p_amt_approved_status)
              status={homeLoanData.p_amt_approved_status || "APPROVAL_AWAITING"}
            />
          </div>

          {/* Interest Paid on Home Loan */}
          <div className="flex items-center mx-3 my-3">
            <div className="flex-no-grow-no-shrink px-1.5 w-[28.4%]">
              <p>Interest Paid on Home Loan</p>
            </div>
            <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%]">
              <p className="text-right text-sm">₹{parseFloat(homeLoanData.interest_amount || 0).toLocaleString()}</p>
            </div>
            <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
              <AttachmentPopover
                attachmentKey={`home-loan-interest-${homeLoanData.home_loan_id}`}
                attachments={attachments}
                onFileSelect={() =>
                  onFileSelect(
                    `home-loan-interest-${homeLoanData.home_loan_id}`,
                    {
                      home_loan_id: homeLoanData.home_loan_id,
                      type: "interest_amount",
                    },
                    homeLoanData.document_count?.interest_amount || 0
                  )
                }
                onAddMoreAttachments={onAddMoreAttachments}
                onRemoveAttachment={onRemoveAttachment}
                onOpenImage={onOpenImage}
                openPopoverKey={openPopoverKey}
                setOpenPopoverKey={setOpenPopoverKey}
                documentCount={homeLoanData.document_count?.interest_amount || 0}
              />
            </div>
            <div className="flex-no-grow-no-shrink px-1.5 w-[14.2%] grid place-items-center">
              <button
                onClick={() =>
                  onCommentClick(`home-loan-interest-${homeLoanData.home_loan_id}`, {
                    home_loan_id: homeLoanData.home_loan_id,
                    type: "interest_amount",
                  })
                }
                className="flex items-center"
              >
                <MdInsertComment className="w-5 h-5 text-neutral-600" />
                {(homeLoanData.comment_count?.interest_amount || 0) > 0 && (
                  <span className="ml-1 text-xs text-gray-600">
                    {homeLoanData.comment_count.interest_amount}
                  </span>
                )}
              </button>
            </div>
            <ApprovalField
              // Read existing approved value using the exact get-user-response field name
              approvedAmount={homeLoanData.interest_amount_approved ?? null}
              actualAmount={parseFloat(homeLoanData.interest_amount || 0)}
              onApproval={(data) => {
                // Send interest approval key for home loan
                onApproval({
                  home_loan_id: homeLoanData.home_loan_id,
                  // Changed from interest_approved_amount to interest_amount_approved
                  interest_amount_approved: data.amount_approved,
                });
              }}
              onRejection={() => {
                onRejection({
                  home_loan_id: homeLoanData.home_loan_id,
                  int_amt_approved_status: "REJECTED",
                });
              }}
              approvalParams={{
                home_loan_id: homeLoanData.home_loan_id,
              }}
              isApproving={isApproving}
              isRejecting={isRejecting}
              // Use the exact status field returned by get-user-response (int_amt_approved_status)
              status={homeLoanData.int_amt_approved_status || "APPROVAL_AWAITING"}
            />
          </div>

          <hr className="my-5 mx-3" />
        </>
      )}
    </>
  );
};

export default RentalIncomeSection;
